import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';

// src/common/openapi.ts
extendZodWithOpenApi(z);
z.config({
  customError: (issue) => {
    switch (issue.code) {
      case "invalid_type":
        return "This field is required";
      case "too_small":
        if (issue.origin === "string") {
          return issue.minimum === 1 ? "This field is required" : `Enter at least ${issue.minimum} characters`;
        }
        if (issue.origin === "number") return `Enter a value of at least ${issue.minimum}`;
        if (issue.origin === "array") return "Select at least one option";
        return void 0;
      case "too_big":
        if (issue.origin === "string") return `Enter at most ${issue.maximum} characters`;
        if (issue.origin === "number") return `Enter a value of at most ${issue.maximum}`;
        return void 0;
      case "invalid_format":
        if (issue.format === "uuid") return "Select an option";
        if (issue.format === "email") return "Enter a valid email address";
        if (issue.format === "url") return "Enter a valid URL";
        return "Enter a valid value";
      default:
        return void 0;
    }
  }
});

// src/common/index.ts
var uuidSchema = z.uuid("Select an option").openapi("Uuid");
z.coerce.number().int().positive().openapi("Id");
var slugSchema = z.string().trim().min(1, "Enter a slug").max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens only").openapi("Slug");
var timestampSchema = z.iso.datetime({ offset: true }).openapi("Timestamp");
z.iso.date().openapi("DateOnly");
var emailSchema = z.email("Enter a valid email address").max(254).openapi("Email");
var phoneSchema = z.string().trim().regex(/^\+?[0-9]{7,15}$/, "Enter a valid phone number").openapi("Phone");
z.string().trim().regex(/^[A-Za-z0-9]{4,10}$/, "Enter a valid pincode").openapi("Pincode");
z.string().trim().regex(/^[A-Z]{2}$/, "Enter a valid country code").openapi("CountryCode");
z.string().trim().regex(/^[A-Z]{3}$/).openapi("CurrencyCode");
var urlSchema = z.url().max(2048).openapi("Url");
z.string().regex(/^\d{1,10}\.\d{2}$/).openapi("Money");
z.string().regex(/^-?\d{1,10}\.\d{2}$/).openapi("SignedMoney");
z.number().min(0).max(100).openapi("Percent");
z.number().int().min(0).openapi("Quantity");
z.string().trim().min(8).max(128).openapi("IdempotencyKey");
var requestIdSchema = z.string().trim().min(1).max(64).openapi("RequestId");
var ErrorCode = {
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  INTERNAL_ERROR: "INTERNAL_ERROR"
};
var errorCodeSchema = z.enum(ErrorCode).openapi("ErrorCode");
var errorDetailSchema = z.object({ path: z.string(), message: z.string() }).openapi("ErrorDetail");
var errorBodySchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  details: z.array(errorDetailSchema).optional(),
  requestId: requestIdSchema
}).openapi("ErrorBody");
z.object({ error: errorBodySchema }).openapi("ErrorResponse");
var MAX_PAGE_SIZE = 100;
z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0)
}).openapi("PaginationMeta");
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(20)
}).openapi("PaginationQuery");
var sortOrderSchema = z.enum(["asc", "desc"]).openapi("SortOrder");
var sortQuerySchema = z.object({
  sort: z.string().trim().min(1).max(64).optional(),
  order: sortOrderSchema.default("desc")
}).openapi("SortQuery");
var searchQuerySchema = z.object({ search: z.string().trim().min(1).max(160).optional() }).openapi("SearchQuery");
var listQuerySchema = paginationQuerySchema.extend(sortQuerySchema.shape).extend(searchQuerySchema.shape).openapi("ListQuery");
z.object({ dateFrom: timestampSchema.optional(), dateTo: timestampSchema.optional() }).openapi("DateRangeQuery");
var booleanQuerySchema = z.union([z.boolean(), z.stringbool()]).openapi("BooleanQuery");
z.object({ id: uuidSchema }).openapi("IdParam");
z.object({ slug: slugSchema }).openapi("SlugParam");
z.object({ ids: z.array(uuidSchema).min(1).max(100) }).openapi("BulkIds");
z.object({ id: uuidSchema, name: z.string(), role: z.string() }).openapi("Actor");
z.object({ data: z.object({ success: z.literal(true) }) }).openapi("SuccessResponse");
z.object({ data: z.object({ count: z.number().int().min(0) }) }).openapi("CountResponse");

// src/auth/index.ts
var UserRole = {
  CUSTOMER: "customer",
  ADMIN: "admin",
  SUPER_ADMIN: "super_admin"
};
var userRoleSchema = z.enum(UserRole).openapi("UserRole");
var UserStatus = {
  ACTIVE: "active",
  INVITED: "invited",
  PENDING_APPROVAL: "pending_approval",
  REJECTED: "rejected",
  SUSPENDED: "suspended",
  DELETED: "deleted"
};
var userStatusSchema = z.enum(UserStatus).openapi("UserStatus");
var AuthProvider = { GOOGLE: "google", EMAIL: "email" };
var otpEmailSchema = emailSchema.openapi("OtpEmail");
var authProviderSchema = z.enum(AuthProvider).openapi("AuthProvider");
var PERMISSION_KEYS = [
  "catalog:read",
  "catalog:write",
  "inventory:read",
  "inventory:write",
  "vehicle:read",
  "vehicle:write",
  "vehicle_type:write",
  "fitment:write",
  "garage:manage",
  "cart:manage",
  "checkout:submit",
  "wishlist:manage",
  "order:create",
  "order:read_own",
  "order:read_all",
  "order:update",
  "order:transition",
  "order:cancel_own",
  "order:cancel_any",
  "payment:read",
  "refund:create",
  "shipment:read",
  "shipment:write",
  "customer:read",
  "customer:write",
  "promotion:read",
  "promotion:write",
  "media:upload",
  "media:delete",
  "report:read",
  "notification:read_own",
  "notification:send",
  "profile:manage",
  "settings:read",
  "settings:write",
  "admin_user:read",
  "admin_user:invite",
  "admin_user:write",
  "audit_log:read",
  "feedback:read"
];
var permissionKeySchema = z.enum(PERMISSION_KEYS).openapi("PermissionKey");
var sessionUserSchema = z.object({
  id: uuidSchema,
  email: emailSchema,
  name: z.string(),
  avatarUrl: urlSchema.nullable(),
  phone: phoneSchema.nullable(),
  role: userRoleSchema,
  status: userStatusSchema,
  permissions: z.array(permissionKeySchema),
  createdAt: timestampSchema
}).openapi("SessionUser");
var authTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1).optional(),
  tokenType: z.literal("Bearer"),
  expiresIn: z.number().int().positive()
}).openapi("AuthTokens");
var accessTokenClaimsSchema = z.object({
  sub: uuidSchema,
  email: emailSchema,
  role: userRoleSchema,
  sid: uuidSchema,
  iat: z.number().int(),
  exp: z.number().int()
}).openapi("AccessTokenClaims");
var deviceLabelSchema = z.string().trim().min(1).max(120).openapi("DeviceLabel");
var otpAudienceSchema = z.enum(["customer", "staff"]).openapi("OtpAudience");
var googleLoginRequestSchema = z.object({
  idToken: z.string().min(1),
  audience: otpAudienceSchema.optional(),
  deviceLabel: deviceLabelSchema.optional()
}).openapi("GoogleLoginRequest");
var googleRegisterRequestSchema = z.object({
  idToken: z.string().min(1),
  name: z.string().trim().min(1).max(120).optional(),
  phone: phoneSchema.optional(),
  acceptedTerms: z.literal(true),
  deviceLabel: deviceLabelSchema.optional()
}).openapi("GoogleRegisterRequest");
var googleIdentitySchema = z.object({
  email: emailSchema,
  name: z.string(),
  avatarUrl: urlSchema.nullable(),
  provider: authProviderSchema
}).openapi("GoogleIdentity");
var registrationRequiredSchema = z.object({ registrationRequired: z.literal(true), identity: googleIdentitySchema }).openapi("RegistrationRequired");
var loginResultSchema = z.object({ user: sessionUserSchema, tokens: authTokensSchema, isNewUser: z.boolean() }).openapi("LoginResult");
var loginResponseSchema = z.union([loginResultSchema, registrationRequiredSchema]).openapi("LoginResponse");
var refreshRequestSchema = z.object({ refreshToken: z.string().min(1).optional(), audience: otpAudienceSchema.optional() }).openapi("RefreshRequest");
var refreshResponseSchema = z.object({ tokens: authTokensSchema, user: sessionUserSchema }).openapi("RefreshResponse");
var logoutRequestSchema = z.object({
  refreshToken: z.string().min(1).optional(),
  audience: otpAudienceSchema.optional(),
  allDevices: z.boolean().default(false)
}).openapi("LogoutRequest");
var profileUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: phoneSchema.nullable(),
  avatarUrl: urlSchema.nullable()
}).partial().openapi("ProfileUpdate");
var sessionSchema = z.object({
  id: uuidSchema,
  deviceLabel: z.string().nullable(),
  createdAt: timestampSchema,
  lastUsedAt: timestampSchema.nullable(),
  expiresAt: timestampSchema,
  revokedAt: timestampSchema.nullable(),
  current: z.boolean()
}).openapi("Session");
var sessionQuerySchema = listQuerySchema.extend({ active: booleanQuerySchema.optional() }).openapi("SessionQuery");
var otpRequestSchema = z.object({ email: otpEmailSchema }).openapi("OtpRequest");
var otpVerifySchema = z.object({
  email: otpEmailSchema,
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
  audience: otpAudienceSchema,
  deviceLabel: deviceLabelSchema.optional(),
  name: z.string().trim().min(1, "Enter your full name").max(120).optional()
}).openapi("OtpVerify");
var adminSignupSchema = z.object({
  email: otpEmailSchema,
  name: z.string().trim().min(1, "Enter your full name").max(120),
  phone: phoneSchema,
  requestedRole: z.enum(["admin"]).default("admin"),
  contactInfo: z.string().trim().max(500).optional()
}).openapi("AdminSignup");
var adminSignupResponseSchema = z.object({
  id: uuidSchema,
  email: emailSchema,
  name: z.string(),
  status: z.literal(UserStatus.PENDING_APPROVAL),
  message: z.string()
}).openapi("AdminSignupResponse");

export { AuthProvider, PERMISSION_KEYS, UserRole, UserStatus, accessTokenClaimsSchema, adminSignupResponseSchema, adminSignupSchema, authProviderSchema, authTokensSchema, deviceLabelSchema, googleIdentitySchema, googleLoginRequestSchema, googleRegisterRequestSchema, loginResponseSchema, loginResultSchema, logoutRequestSchema, otpAudienceSchema, otpEmailSchema, otpRequestSchema, otpVerifySchema, permissionKeySchema, profileUpdateSchema, refreshRequestSchema, refreshResponseSchema, registrationRequiredSchema, sessionQuerySchema, sessionSchema, sessionUserSchema, userRoleSchema, userStatusSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map